import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useState } from 'react';
import { useInvalidateBanking } from '@/components/banking/line-editor';
import { RuleDrawer } from '@/components/banking/rule-drawer';
import { ErrorBanner } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useBankAccounts, useBankRules, type BankRule } from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/banking/rules')({ component: RulesPage });

const OP_LABEL: Record<string, string> = {
  contains: 'contains',
  not_contains: "doesn't contain",
  equals: 'is',
  starts_with: 'starts with',
  greater_than: 'is greater than',
  less_than: 'is less than',
};
const APPLIES: Record<BankRule['appliesTo'], string> = {
  money_in: 'Money in',
  money_out: 'Money out',
  both: 'Money in or out',
};

function describeConditions(r: Pick<BankRule, 'conditions' | 'matchAll'>): string {
  return r.conditions
    .map(
      (c) =>
        `${c.field === 'amount' ? 'Amount' : 'Bank text'} ${OP_LABEL[c.op] ?? c.op} ${
          c.field === 'amount' ? formatAmount(c.value) : `“${c.value}”`
        }`,
    )
    .join(r.matchAll ? ' and ' : ' or ');
}

/** Screen B3 (document 06): bank rules in priority order. */
function RulesPage() {
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const rules = useBankRules();
  const banks = useBankAccounts();
  const invalidate = useInvalidateBanking();
  const [drawer, setDrawer] = useState<{ open: boolean; rule: BankRule | null }>({
    open: false,
    rule: null,
  });
  const [deleting, setDeleting] = useState<BankRule | null>(null);

  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      api<BankRule[]>(`/orgs/${orgId}/banking/rules/order`, {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => invalidate(),
  });
  const remove = useMutation({
    mutationFn: (r: BankRule) => api(`/orgs/${orgId}/banking/rules/${r.id}`, { method: 'DELETE' }),
    onSuccess: async (_x, r) => {
      await invalidate();
      toast(`Rule "${r.name}" deleted.`);
      setDeleting(null);
    },
  });
  const move = (i: number, by: -1 | 1) => {
    const ids = (rules.data ?? []).map((r) => r.id);
    const j = i + by;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };

  return (
    <>
      <PageHeader
        title="Rules"
        description="Rules categorise bank transactions for you. The first rule that applies wins, from the top."
        actions={
          canEdit ? (
            <Button onClick={() => setDrawer({ open: true, rule: null })}>New rule</Button>
          ) : null
        }
      />
      <ErrorBanner>{reorder.error?.message ?? null}</ErrorBanner>
      {!orgId ? (
        <EmptyState title="Select a client" />
      ) : rules.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rules.isError ? (
        <EmptyState title="Could not load rules" hint={rules.error.message} />
      ) : rules.data.length === 0 ? (
        <EmptyState
          title="No rules yet"
          hint="Create a rule for transactions that come up every month, such as DEWA, du or bank charges."
          action={
            canEdit ? (
              <Button onClick={() => setDrawer({ open: true, rule: null })}>New rule</Button>
            ) : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[90px]">Priority</TableHead>
                <TableHead>Rule name</TableHead>
                <TableHead>Applied to</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Settings</TableHead>
                <TableHead className="w-[110px] text-right">In For review</TableHead>
                <TableHead className="w-[150px] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.data.map((r, i) => (
                <TableRow key={r.id} data-testid="rule-row">
                  <TableCell>
                    <span className="inline-flex items-center gap-1">
                      <span className="tnum w-5 text-right">{r.priority}</span>
                      {canEdit ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label={`Move ${r.name} up`}
                            disabled={i === 0 || reorder.isPending}
                            onClick={() => move(i, -1)}
                          >
                            <ArrowUp />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            aria-label={`Move ${r.name} down`}
                            disabled={i === rules.data.length - 1 || reorder.isPending}
                            onClick={() => move(i, 1)}
                          >
                            <ArrowDown />
                          </Button>
                        </>
                      ) : null}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">
                    {r.name}
                    {r.autoAdd ? (
                      <Badge variant="secondary" className="ml-2">
                        Auto-add
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <div>{APPLIES[r.appliesTo]}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.bankAccountName ?? 'All bank accounts'}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[320px] whitespace-normal text-sm">
                    {describeConditions(r)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.action === 'exclude' ? (
                      'Exclude'
                    ) : (
                      <>
                        <div>{r.accountName}</div>
                        <div className="text-xs text-muted-foreground">
                          {[r.taxCode ? `VAT ${r.taxCode}` : null, r.contactName]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="tnum text-right">{r.suggestedCount}</TableCell>
                  <TableCell className="text-right">
                    {canEdit ? (
                      <>
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2"
                          onClick={() => setDrawer({ open: true, rule: r })}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="link"
                          className="px-2 text-destructive"
                          onClick={() => setDeleting(r)}
                        >
                          Delete
                        </Button>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RuleDrawer
        open={drawer.open}
        rule={drawer.rule}
        banks={banks.data ?? []}
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
      <Dialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete the rule “{deleting?.name}”?</DialogTitle>
            <DialogDescription>
              Transactions it already categorised stay as they are. It stops suggesting categories
              for transactions in For review.
            </DialogDescription>
          </DialogHeader>
          {remove.error ? (
            <p role="alert" className="text-sm text-destructive">
              {remove.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
